import { Vue, Component, Emit, Watch, Prop } from 'vue-property-decorator';
import { differenceBy, forEach, intersectionBy, map, pick, set, uniqBy } from 'lodash';
import { MENU_ACTIONS, NOTIFICATION, PROJECT_TYPES, USER_ENGAGEMENT_ROLES, USER_ENGAGEMENT_SOURCES } from '@/constants';
import { Getter } from '@/decorators';
import { ActionMenuItem, ProjectDTO, UserDTO, UserEngagementDTO } from '@/models';
import ActionMenu from '@/components/common/action-menu/ActionMenu.vue';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import ErrorDialog from './error-dialog/ErrorDialog.vue';
import ImportTeamMemberService, { Field, FieldKey } from '@/services/import-team-member';
import MapDataInput from './map-data-input/MapDataInput.vue';
import { DialogProps } from './error-dialog/error-dialog';

interface MapDataInputProp {
  display: string;
  placeholder: string;
  model: string;
  errorMessage?: string;
}

interface ImportedData {
  data: UserEngagementDTO[];
  errors: string[];
}

interface ErrorData {
  isDifferenceKey: boolean;
  resolveData1: string | number | UserEngagementDTO | null;
  resolveData2: string | number | UserEngagementDTO | null;
  sameMemberId?: string;
}

@Component({
  components: {
    ActionMenu,
    MapDataInput,
    ConfirmationDialog,
    ErrorDialog
  }
})
export default class ImportTeamMember extends Vue {
  @Prop() members: UserEngagementDTO[];
  @Prop() projectAssessmentId: number;
  @Prop() isProjectTeamMemberEmpty: boolean;
  @Getter('app/currentUser') currentUser: UserDTO;
  @Getter('project/project') project: ProjectDTO;
  @Getter('user/users') users: UserDTO[];

  private clearAllDialogShown = false;
  private importDialogShown = false;

  private isUploadFileSuccess = false;
  private file: File = (null as unknown) as File;
  private fieldColumnMapping: Field[] = [];
  private teamMemberService: ImportTeamMemberService;
  private columnMappingErrorMessage = '';
  private isFileReading = false;
  private importedData: ImportedData = { data: [], errors: [] };
  private filteredMembers = [] as UserEngagementDTO[];
  private errorDialog = {
    props: {} as DialogProps,
    promiseQueue: [] as ErrorData[],
    visible: false,
    resolve: null as any,
    reject: null as any,
    changedKey: '' as string | number,
    isCancel: false
  };

  get menuItems(): ActionMenuItem[] {
    if (this.currentUser.isAdmin || this.currentUser.id === this.project.manager.id) {
      return [
        {
          label: MENU_ACTIONS.IMPORT_MEMBERS,
          icon: 'mdi-file-table',
          handler: this.onImportTeamMember
        },
        {
          label: MENU_ACTIONS.CLEAR_ALL,
          icon: 'mdi-delete',
          handler: this.openClearAllConfirmationDialog,
          disabled: this.isProjectTeamMemberEmpty
        }
      ];
    }
    return [];
  }

  get columnMaps(): MapDataInputProp[] {
    if (!this.fieldColumnMapping) {
      return [];
    } else {
      const fieldColumnMapping = this.fieldColumnMapping.map(({ value, display }) => {
        return {
          display,
          placeholder: value ? value : 'A'
        };
      }) as MapDataInputProp[];
      return fieldColumnMapping;
    }
  }

  get hasColumnMappingError(): boolean {
    return !!this.columnMappingErrorMessage;
  }

  get isUniqImportedMemberKey(): boolean {
    if (this.importedData.data.length === 0) {
      return true;
    }
    const uniqByKeys = uniqBy(this.importedData.data, 'key');
    return uniqByKeys.length === 1;
  }

  @Watch('file', { deep: true })
  onFileChange(): void {
    if (this.file) {
      this.isUploadFileSuccess = true;
      this.teamMemberService.updateFile(this.file);
    } else {
      this.isUploadFileSuccess = false;
    }
  }

  @Watch('fieldColumnMapping', { deep: true })
  onFieldColumnMappingChanged(): void {
    this.updateFieldColumnMapping();
    this.checkDuplicateColumnMapping();
  }

  @Watch('importedData.errors', { deep: true })
  onImportedDataHasError(): void {
    if (this.importedData.errors.length > 0) {
      const data = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: NOTIFICATION.TEXT.importMemberFailed,
        content: this.importedData.errors,
        icon: null,
        multiline: true,
        height: 0
      };
      this.$store.commit('notification/setNotification', data);
    }
  }

  @Watch('importedData', { deep: true })
  async onMemberChanged(): Promise<void> {
    this.filteredMembers = [];
    this.isFileReading = true;
    if (!this.isUniqImportedMemberKey) {
      this.notifyWhenNotSameKey();
      return;
    }
    await this.handleError();
    if (this.importedData.errors.length === 0 && !this.errorDialog.isCancel) {
      this.onImportSuccess();
      this.closeDialogWhenImportSuccess();
    }
    this.isFileReading = false;
  }

  onImportTeamMember(): void {
    this.importDialogShown = true;
  }

  openClearAllConfirmationDialog(): void {
    this.clearAllDialogShown = true;
  }

  //#region file process
  setFile(file?: File): void {
    if (!file) {
      this.file = (null as unknown) as File;
    } else {
      this.file = file;
    }
  }

  onCloseFormInput(): void {
    this.importDialogShown = false;
    this.setFile();
  }

  onInput(file: File): void {
    this.setFile(file);
  }

  onClearFile(): void {
    this.setFile();
  }

  //#endregion

  generateFieldColumnMappingByType(projectType: string): void {
    if (!this.teamMemberService) {
      return;
    } else {
      this.fieldColumnMapping = ImportTeamMemberService.initFieldToColumn(projectType);
    }
  }

  updateFieldColumnMapping(): void {
    const changedMapping = {};
    forEach(this.fieldColumnMapping, ({ name, value }) => {
      set(changedMapping, name, value);
    });
    this.teamMemberService.updateFieldColumnMapping(changedMapping);
  }

  checkDuplicateColumnMapping(): void {
    this.columnMappingErrorMessage = '';
    forEach(this.fieldColumnMapping, ({ value }) => {
      const isDuplicated = this.fieldColumnMapping.filter(field => field.value === value).length > 1;
      if (isDuplicated) {
        this.columnMappingErrorMessage = `Column ${value} exists only one !`;
        return;
      }
    });
  }

  setImportedData(importedData: ImportedData): void {
    this.importedData.data = importedData.data;
    this.importedData.errors = importedData.errors;
  }

  async readFile(): Promise<void> {
    this.isFileReading = true;
    await this.teamMemberService.execute();
    const { errors, importedData } = this.teamMemberService.result;
    this.setImportedData({ errors, data: importedData });
    this.isFileReading = false;
  }

  closeDialogWhenImportSuccess(): void {
    this.setFile();
    this.importDialogShown = false;
    const data = {
      showing: true,
      type: NOTIFICATION.TYPE.success,
      title: NOTIFICATION.TEXT.importMemberSuccess,
      content: [''],
      icon: null,
      multiline: false,
      height: 0
    };
    this.$store.commit('notification/setNotification', data);
  }

  notifyWhenNotSameKey(): void {
    const data = {
      showing: true,
      type: NOTIFICATION.TYPE.error,
      title: NOTIFICATION.TEXT.importMemberFailed,
      content: [NOTIFICATION.TEXT.dataHaveDifferentKey],
      icon: null,
      multiline: true,
      height: 0
    };
    this.$store.commit('notification/setNotification', data);
  }

  notifyCancelImport(): void {
    const data = {
      showing: true,
      type: NOTIFICATION.TYPE.success,
      title: NOTIFICATION.TEXT.importCancel,
      content: [''],
      icon: null,
      multiline: false,
      height: 0
    };
    this.$store.commit('notification/setNotification', data);
  }

  @Emit('import:success')
  onImportSuccess(): UserEngagementDTO[] {
    const compareKey = 'user.id';
    const membersInProps = differenceBy(this.members, this.filteredMembers, compareKey);
    const importedData = differenceBy(this.importedData.data, this.filteredMembers, compareKey);
    const filteredMembers = differenceBy(this.filteredMembers, [...membersInProps, ...importedData]);
    const changedMember = [...membersInProps, ...importedData, ...filteredMembers];
    let memberResult = changedMember as UserEngagementDTO[];
    if (this.project.projectType === PROJECT_TYPES.INDIVIDUAL_TRACKING) {
      memberResult = memberResult.map(member => ({
        ...member,
        userEngagementRole: USER_ENGAGEMENT_ROLES.PROJECT_MEMBER
      }));
    }
    if (this.errorDialog.changedKey) {
      memberResult = memberResult.map(member => ({
        ...member,
        key: this.errorDialog.changedKey
      })) as UserEngagementDTO[];
    }
    return memberResult;
  }

  @Emit('clearAllMember')
  clearAllMember(): void {
    this.clearAllDialogShown = false;
  }

  getBoldText(text: string): string {
    return `<span class="text--bolder">${text}</span>`;
  }

  handleDifferenceKeyInMemberAndImportedMember(): void {
    if (this.members.length === 0 || this.importedData.data.length === 0) {
      return;
    }
    const memberKey = this.members[0].key;
    const importedMemberKey = this.importedData.data[0].key;
    if (!memberKey || !importedMemberKey) {
      return;
    }
    if (memberKey === importedMemberKey) {
      return;
    } else {
      this.errorDialog.promiseQueue.push({
        isDifferenceKey: true,
        resolveData1: memberKey,
        resolveData2: importedMemberKey
      });
    }
  }

  handleSameMemberWithImportedMember(): void {
    if (this.importedData.errors.length > 0) {
      return;
    }
    if (this.members.length === 0 || this.importedData.data.length === 0) {
      return;
    }

    const initKeys = Object.keys(FieldKey);
    const mapMembers = map(this.members, member => pick(member, initKeys));
    const mapImportedData = map(this.importedData.data, member => pick(member, initKeys));
    const sameMembers = intersectionBy(mapMembers, mapImportedData, 'user.id') as UserEngagementDTO[];

    sameMembers.map((member: UserEngagementDTO) => {
      const {
        user: { id }
      } = member;
      const importedMember = this.importedData.data.find(member => member.user.id === id);
      this.errorDialog.promiseQueue.push({
        isDifferenceKey: false,
        resolveData1: member,
        resolveData2: importedMember || null
      });
    });
  }

  async createNewPromise(callback: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.errorDialog.resolve = resolve;
      this.errorDialog.reject = reject;
      callback();
    }).catch(() => {
      this.errorDialog.visible = false;
      this.filteredMembers = [];
      this.errorDialog.isCancel = true;
    });
  }

  onChangeKey(key: string | number): void {
    this.errorDialog.changedKey = key;
  }

  initErrorDialog(): void {
    this.errorDialog.changedKey = '';
    this.errorDialog.promiseQueue = [];
    this.errorDialog.reject = null;
    this.errorDialog.resolve = null;
    this.errorDialog.isCancel = false;
  }

  async handleError(): Promise<void> {
    this.initErrorDialog();
    this.handleDifferenceKeyInMemberAndImportedMember();
    this.handleSameMemberWithImportedMember();

    this.errorDialog.visible = true;
    for (const errorData of this.errorDialog.promiseQueue) {
      await this.createNewPromise(() => {
        const onReject = () => {
          this.errorDialog.visible = false;
          this.errorDialog.reject('Cancel import');
          this.isFileReading = false;
        };
        if (errorData.isDifferenceKey) {
          const onExistingDataChosen = () => {
            this.errorDialog.resolve(errorData.resolveData1);
            this.onChangeKey((errorData.resolveData1 as string) || '');
          };
          const onImportedDataChosen = () => {
            this.errorDialog.resolve(errorData.resolveData2);
            this.onChangeKey((errorData.resolveData2 as string) || '');
          };
          this.errorDialog.props = {
            title: 'Difference key',
            body:
              'The KEY in your imported data is different from the existing data. Please choose only one KEY for this project.',
            footer: {
              reject: {
                title: 'Cancel import',
                onClick: onReject
              },
              existingResolve: {
                title: `KEY = ${errorData.resolveData1}`,
                onClick: onExistingDataChosen
              },
              importedResolve: {
                title: `KEY = ${errorData.resolveData2}`,
                onClick: onImportedDataChosen
              }
            }
          };
        } else {
          const onExistingDataChosen = () => {
            this.errorDialog.resolve(errorData.resolveData1);
            this.filteredMembers.push(errorData.resolveData1 as UserEngagementDTO);
          };
          const onImportedDataChosen = () => {
            this.errorDialog.resolve(errorData.resolveData2);
            this.filteredMembers.push(errorData.resolveData2 as UserEngagementDTO);
          };
          const member = errorData.resolveData1 as UserEngagementDTO;
          this.errorDialog.props = {
            body: [
              'Members with',
              this.getBoldText(`ID ${member.user.code}`),
              'are existed in the table. Do you want to replace them with the imported data?'
            ].join(' '),
            title: 'Imported members are existed',
            footer: {
              reject: {
                title: 'Cancel import',
                onClick: onReject
              },
              existingResolve: {
                title: 'Keep',
                onClick: onExistingDataChosen
              },
              importedResolve: {
                title: 'Confirm',
                onClick: onImportedDataChosen
              }
            }
          };
        }
      });
    }
    this.errorDialog.visible = false;
  }

  mounted(): void {
    if (this.project) {
      const { projectType } = this.project;
      this.teamMemberService = new ImportTeamMemberService({
        file: this.file,
        projectType: projectType,
        users: this.users
      });
      this.generateFieldColumnMappingByType(projectType);
    }
  }
}
